import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ENV_REACT_APP } from "../Configurations/ENV_Config";

export interface OwnerPhotoItem {
  _id: string;
  fileName: string;
  uploadedAt: string;
  url: string;
}

const apiBase = (path: string) =>
  `${ENV_REACT_APP?.REACT_APP_API_BASE_URL || ""}${path}`;

/**
 * Fetches owner-uploaded photos for a property.
 *
 * Owner photos are a PUBLIC library — visible to everyone (logged in or not).
 * The token is still sent when present, but the response is the same either
 * way. Used by the property photo slider and any other surface that needs
 * owner photos.
 */
export const useOwnerPhotos = (propertyId?: string, authToken?: string) => {
  const [ownerPhotos, setOwnerPhotos] = useState<OwnerPhotoItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const reload = useCallback(async () => {
    if (!propertyId) {
      setOwnerPhotos([]);
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(
        apiBase(`/api/user/property/${propertyId}/owner-photos`),
        authToken
          ? { headers: { Authorization: `Bearer ${authToken}` } }
          : undefined
      );
      setOwnerPhotos(res?.data?.data?.photos || []);
    } catch {
      // Failures (network, etc.) just yield no owner photos in the slider —
      // the placeholder still renders.
      setOwnerPhotos([]);
    } finally {
      setIsLoading(false);
    }
  }, [propertyId, authToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { ownerPhotos, isLoading, reload };
};
